import { browser } from '$app/environment';
import { auth, provider, db } from '$lib/firebase';
import { onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth';
import { doc as firestoreDoc, getDoc } from 'firebase/firestore';
import type { AdminLookup, RolePermissions } from '../../../../shared/src/schemas/types';
import { stopImpersonating } from './rolePermissions.svelte';

interface UserState {
	user: User | null;
	loading: boolean;
	error: string | null;
	isAdmin?: boolean;
	adminRole?: 'admin' | 'super' | 'scoped';
	schoolId?: string;
	adminScope?: unknown;
	/** Per-person data visibility overrides from the admin grant, if any. */
	adminDataPermissions?: Partial<RolePermissions>;
	/**
	 * Full lookups/admins doc, kept only for admins. Lets impersonation resolve
	 * the impersonated person's admin entry (see $lib/state/effectiveUser).
	 */
	adminLookup?: AdminLookup;
}

// Svelte 5 reactive state using runes
export const userState: UserState = $state({
	user: null,
	loading: true,
	error: null,
	isAdmin: false,
	adminRole: undefined,
	schoolId: undefined,
	adminScope: undefined
});

// Initialize auth state listener only in browser (skip in Storybook so stories can mock auth)
if (browser && auth && !import.meta.env.STORYBOOK) {
	onAuthStateChanged(auth, async (firebaseUser: User | null) => {
		// Back to loading until the admin lookup below settles. Without this there
		// is a window — after a sign-in, when `loading` is already false from the
		// previous run — where `user` is set but `isAdmin` is still false, and
		// anything that gates on "signed in and not loading" reads the user as a
		// non-admin. A deep link straight into /admin/* after sign-in lands in
		// exactly that window and fails with "Not authorized".
		userState.loading = true;
		userState.user = firebaseUser;
		// default
		userState.isAdmin = false;
		userState.adminRole = undefined;
		userState.schoolId = undefined;
		userState.adminScope = undefined;

		// If we have a signed-in user, check the lookups/admins doc to see if they're an admin
		if (firebaseUser && db) {
			const normalizedEmail = firebaseUser.email?.trim().toLowerCase();
			if (normalizedEmail) {
				try {
					const adminDocRef = firestoreDoc(db, 'lookups', 'admins');
					const adminSnap = await getDoc(adminDocRef);
					if (adminSnap.exists()) {
						const data = adminSnap.data() as AdminLookup | undefined;
						const adminEntry = data?.[normalizedEmail];
						if (adminEntry) {
							userState.isAdmin = true;
							userState.adminLookup = data;
							userState.adminRole = adminEntry.role;
							userState.schoolId = adminEntry.school_id;
							userState.adminScope = adminEntry.scope;
							userState.adminDataPermissions = adminEntry.dataPermissions;
						}
					}
				} catch (err: unknown) {
					console.warn('admin lookup failed', err instanceof Error ? err.message : err);
				}
			}
		}

		userState.loading = false;
	});
}

// Sign in with Google
export async function signInWithGoogle(): Promise<User> {
	if (!auth) throw new Error('Auth not initialized');

	try {
		userState.loading = true;
		userState.error = null;
		const result = await signInWithPopup(auth, provider);
		// default
		userState.isAdmin = false;
		userState.adminRole = undefined;
		userState.schoolId = undefined;
		userState.adminScope = undefined;
		const firebaseUser = result.user;
		if (firebaseUser && db) {
			const normalizedEmail = firebaseUser.email?.trim().toLowerCase();
			if (normalizedEmail) {
				try {
					const adminDocRef = firestoreDoc(db, 'lookups', 'admins');
					const adminSnap = await getDoc(adminDocRef);
					if (adminSnap.exists()) {
						const data = adminSnap.data() as AdminLookup | undefined;
						const adminEntry = data?.[normalizedEmail];
						if (adminEntry) {
							userState.isAdmin = true;
							userState.adminLookup = data;
							userState.adminRole = adminEntry.role;
							userState.schoolId = adminEntry.school_id;
							userState.adminScope = adminEntry.scope;
							userState.adminDataPermissions = adminEntry.dataPermissions;
						}
					}
				} catch (err: unknown) {
					console.warn('admin lookup failed', err instanceof Error ? err.message : err);
				}
			}
		}
		return firebaseUser;
	} catch (error) {
		userState.error = error instanceof Error ? error.message : 'Sign-in failed';
		console.error('Sign-in error:', error);
		throw error;
	} finally {
		userState.loading = false;
	}
}

// Sign out
export async function signOutUser(): Promise<void> {
	if (!auth) throw new Error('Auth not initialized');

	try {
		userState.loading = true;
		userState.error = null;
		// Impersonation outlives a reload now; it must not outlive the account.
		stopImpersonating();
		await signOut(auth);
	} catch (error) {
		userState.error = error instanceof Error ? error.message : 'Sign-out failed';
		console.error('Sign-out error:', error);
		throw error;
	} finally {
		userState.loading = false;
	}
}
